import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

dayjs.extend(customParseFormat);

import DatePicker from '@app/components/ui/DatePicker';
import { Input } from '@app/components/ui/Input';
import Modal, { ModalProps } from '@app/components/ui/Modal/Modal';
import Select, { SelectOption } from '@app/components/ui/Select';
import Textarea from '@app/components/ui/Textarea';
import { TAX_RESPONSIBILITY } from '@app/constants/transaction';
import { useAuth } from '@app/hooks/useAuth';
import { cn } from '@app/libs/utils';
import { transactionService } from '@app/services/transaction.service';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import { Button, useToast } from '../ui';
import Checkbox from '../ui/Checkbox';
import Dropdown from '../ui/Dropdown';

interface TransactionModalProps extends Partial<ModalProps> {
  property?: any;
  transaction?: any;
  onUpdate?: (...args: any[]) => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  property,
  transaction,
  onUpdate,
  ...props
}) => {
  const { info: user } = useAuth();

  const [transferPrice, setTransferPrice] = useState<string>(
    transaction?.propertyValue || property?.price
  );
  const [taxResponsibility, setTaxResponsibility] = useState<SelectOption | null>(null);
  const [assetHandoverTime, setAssetHandoverTime] = useState<Date | null>(null);
  const [paperworkHandoverTime, setPaperworkHandoverTime] = useState<Date | null>(null);
  const [depositAmount, setDepositAmount] = useState<string>('100000000');
  const [depositTime, setDepositTime] = useState<Date | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [otherTerms, setOtherTerms] = useState<string>('');
  const [agree, setAgree] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();
  const isVerified = !!user?.isVerified;

  const canSubmit = useMemo(() => {
    if (!isVerified) return false;
    return (
      !!transferPrice &&
      !!taxResponsibility &&
      !!assetHandoverTime &&
      !!paperworkHandoverTime &&
      !!depositAmount &&
      !!depositTime &&
      agree &&
      !submitting
    );
  }, [
    transferPrice,
    taxResponsibility,
    assetHandoverTime,
    paperworkHandoverTime,
    depositAmount,
    depositTime,
    agree,
    submitting,
  ]);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!transferPrice) next.transferPrice = 'Vui lòng nhập giá chuyển nhượng';
    if (!taxResponsibility) next.taxResponsibility = 'Vui lòng chọn trách nhiệm thuế, phí';
    if (!assetHandoverTime) next.assetHandoverTime = 'Vui lòng chọn thời điểm bàn giao tài sản';
    if (!paperworkHandoverTime)
      next.paperworkHandoverTime = 'Vui lòng chọn thời điểm bàn giao giấy tờ';
    if (!depositAmount) next.depositAmount = 'Vui lòng nhập tiền đặt cọc';
    if (!depositTime) next.depositTime = 'Vui lòng chọn thời gian đặt cọc';
    if (!agree) next.agree = 'Vui lòng đọc và đồng ý với điều khoản và chính sách của PropX';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    if (transaction) {
      onUpdate?.({
        ...transaction,
        propertyValue: transferPrice,
        taxType: taxResponsibility?.value,
        transferPropertyDate: dayjs(assetHandoverTime).format('DD/MM/YYYY'),
        transferPropertyDocDate: dayjs(paperworkHandoverTime).format('DD/MM/YYYY'),
        depositAmount: depositAmount,
        depositTime: dayjs(depositTime).format('DD/MM/YYYY'),
        notePayment: paymentTerms,
        note: otherTerms,
      });

      return;
    }

    try {
      setSubmitting(true);
      const { success, errors } = await transactionService.create({
        propertyValue: transferPrice,
        depositAmount: depositAmount,
        depositDate: dayjs(depositTime).format('DD/MM/YYYY'),
        publishCertificateNumber: property.publishCertificateNumber,
        newsId: property.id,
        taxType: taxResponsibility?.value,
        transferPropertyDate: dayjs(assetHandoverTime).format('DD/MM/YYYY'),
        transferPropertyDocDate: dayjs(paperworkHandoverTime).format('DD/MM/YYYY'),
        notePayment: paymentTerms,
        note: otherTerms,
      });
      if (success) {
        toast.success('Yêu cầu giao dịch đã được gửi thành công');

        onClose?.();
      } else {
        toast.error(errors?.message || 'Lỗi khi gửi yêu cầu giao dịch. Vui lòng thử lại.');
      }
    } catch (error) {
      toast.error('Lỗi khi gửi yêu cầu giao dịch. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (transaction) {
      setTransferPrice(transaction.propertyValue);
      setTaxResponsibility(
        TAX_RESPONSIBILITY.find((opt: any) => opt.value === transaction.taxType) || null
      );

      // Parse dates với format DD/MM/YYYY nếu là string, hoặc parse như Date object nếu đã là Date
      const parseDate = (dateValue: any) => {
        if (!dateValue) return null;
        if (dateValue instanceof Date) return dateValue;
        if (typeof dateValue === 'string') {
          // Thử parse với format DD/MM/YYYY trước
          const parsed = dayjs(dateValue, 'DD/MM/YYYY', true);
          if (parsed.isValid()) return parsed.toDate();
          // Nếu không được, thử parse như ISO string hoặc default
          return dayjs(dateValue).toDate();
        }
        return dayjs(dateValue).toDate();
      };

      setAssetHandoverTime(parseDate(transaction.transferPropertyDate));
      setPaperworkHandoverTime(parseDate(transaction.transferPropertyDocDate));
      setDepositAmount(transaction.depositAmount);
      setDepositTime(parseDate(transaction.depositTime));
      setPaymentTerms(transaction.notePayment);
      setOtherTerms(transaction.note);
      setAgree(false);
    }
  }, [transaction]);

  return (
    <Modal
      isOpen={isOpen ?? false}
      onClose={onClose ?? (() => {})}
      title="Yêu cầu giao dịch"
      size="full"
      position="center"
      animation="zoom"
      contentClassName="w-235 max-w-full mx-auto max-h-[90vh] overflow-y-auto"
      {...props}
    >
      <div className="flex flex-col gap-6 w-235 max-w-full mx-auto text-gray-700">
        <p className="text-sm leading-5 text-blue-800 whitespace-pre-line italic">
          {`Thông tin bạn nhập sẽ được gửi đến người bán để thực hiện yêu cầu giao dịch.
Khi gửi yêu cầu, bạn đồng ý cho phép người bán xem thông tin cá nhân của mình trong quá trình tạo hồ sơ giao dịch.
Khi người bán chấp thuận, cả hai bên có thể tạo hồ sơ giao dịch và xem thông tin cá nhân của nhau.
Bạn có thể hủy thương lượng bất kỳ lúc nào.`}
        </p>

        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 gap-7.5">
            <Input
              label="Giá chuyển nhượng"
              placeholder="Nhập giá chuyển nhượng"
              type="number"
              value={transferPrice}
              onChange={setTransferPrice}
              containerClassName="text-gray-700"
              required
              showTextNumber
              integer
              units="VND"
              disabled={!!transaction}
            />

            <Dropdown
              options={TAX_RESPONSIBILITY}
              onOptionClick={(opt: any) => setTaxResponsibility(opt)}
              triggerClassName="w-full"
              disabled={!!transaction}
            >
              <div className="text-sm text-gray-700 select-none w-full flex flex-col gap-2">
                <label className="text-sm font-medium">
                  Trách nhiệm thuế, phí<span className="text-red-500 ml-1">*</span>
                </label>
                <div className="px-3 py-2 h-10 border border-gray-200 rounded-lg text-sm text-gray-700 flex items-center justify-between  group-[.disabled]:text-gray-400 group-[.disabled]:bg-gray-100">
                  {TAX_RESPONSIBILITY.find((opt: any) => opt.value === taxResponsibility?.value)
                    ?.label || 'Chọn trách nhiệm thuế, phí'}
                  <ArrowDownIcon className="w-4 h-4 text-gray-700 group-[.open]:rotate-180 transition-transform" />
                </div>
              </div>
            </Dropdown>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2 order-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  Thời điểm bàn giao tài sản
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <DatePicker
                  isRange={false}
                  value={{ startDate: assetHandoverTime, endDate: null }}
                  onChange={range => setAssetHandoverTime(range.startDate)}
                  placeholder="Nhập thời điểm bàn giao tài sản"
                  disabled={!isVerified || !!transaction}
                  presets={[]}
                  minDate={dayjs().toDate()}
                />
                {errors?.assetHandoverTime && (
                  <span className="text-xs text-red-600">{errors.assetHandoverTime}</span>
                )}
              </div>

              <div className="flex flex-col gap-2 order-1">
                <label className="text-sm font-medium flex items-center gap-1">
                  Thời điểm bàn giao giấy tờ
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <DatePicker
                  isRange={false}
                  value={{ startDate: paperworkHandoverTime, endDate: null }}
                  onChange={range => setPaperworkHandoverTime(range.startDate)}
                  placeholder="Nhập thời điểm bàn giao giấy tờ"
                  disabled={!isVerified || !!transaction}
                  presets={[]}
                  minDate={dayjs().toDate()}
                />
                {errors?.paperworkHandoverTime && (
                  <span className="text-xs text-red-600">{errors.paperworkHandoverTime}</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tiền đặt cọc"
                placeholder="Nhập số tiền đặt cọc"
                type="number"
                value={depositAmount}
                onChange={setDepositAmount}
                required
                showTextNumber
                units="VND"
                containerClassName="text-gray-700"
              />

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-1">
                  Thời điểm đặt cọc
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <DatePicker
                  isRange={false}
                  value={{ startDate: depositTime, endDate: null }}
                  onChange={range => setDepositTime(range.startDate)}
                  placeholder="Nhập thời điểm đặt cọc"
                  disabled={!isVerified}
                  minDate={dayjs().toDate()}
                  presets={[]}
                  maxDate={
                    paperworkHandoverTime ? dayjs(paperworkHandoverTime).toDate() : undefined
                  }
                />
                {errors?.depositTime && (
                  <span className="text-xs text-red-600">{errors.depositTime}</span>
                )}
              </div>
            </div>
          </div>

          <Textarea
            label="Điều khoản thanh toán"
            placeholder={'Ví dụ: Thanh toán 50% khi ký hợp đồng, 50% khi bàn giao sổ đỏ'}
            content={paymentTerms}
            onChange={setPaymentTerms}
            maxLength={2000}
            autoResize
          />

          <Textarea
            label="Điều khoản khác"
            placeholder={'Ví dụ: '}
            content={otherTerms}
            onChange={setOtherTerms}
            maxLength={2000}
            autoResize
          />
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            checked={agree}
            onChange={setAgree}
            disabled={!isVerified}
            labelClassName="text-sm text-gray-700"
            label={
              <span className="text-sm text-gray-700">
                Tôi đồng ý với các{' '}
                <Link
                  href="/term"
                  target="_blank"
                  className="underline hover:text-blue-800 transition-all"
                >
                  điều khoản và chính sách
                </Link>{' '}
                của PropX khi thực hiện giao dịch.
              </span>
            }
          />
          {errors?.agree && <div className="text-xs text-red-600">{errors.agree}</div>}
        </div>

        <div className="flex items-center justify-center gap-6 pt-2 text-sm">
          <Button variant="secondary" outline onClick={onClose}>
            Hủy bỏ
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Đang gửi...' : transaction ? 'Thay đổi thỏa thuận' : 'Gửi yêu cầu'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TransactionModal;
